/**
 * Telling a rate quoted on the workspace's own carrier account from one quoted
 * on Skupreme's. The match is by account number, never by carrier name: a
 * workspace with its own FedEx account still gets FedEx rates from ours.
 */

/**
 * Carriers whose rates name the account that quoted them by the same number
 * the workspace saved in settings: FedEx and USPS `account_number`, UPS
 * `shipper_number`. Other carriers' rates carry an internal id or nothing, so
 * they are never marked.
 */
export const OWN_ACCOUNT_CARRIERS = ['fedex', 'ups', 'usps'] as const;

/** Carrier → the account number the workspace connected for it. */
export type ConnectedCarrierAccounts = Record<string, string>;

type ExistingField = { key?: string; value?: unknown };

/** One row of `GET /shipping-labels/carriers`, as far as this needs it. */
export type CarrierSettingsEntry = {
  carrier?: { value?: string };
  credentials?: boolean;
  existing_fields?: {
    account_number?: ExistingField;
    shipper_number?: ExistingField;
  } & Record<string, unknown>;
};

/** A rate as the API sends it, as far as this needs it. */
export type QuotedRate = {
  credentials_id?: string | null;
  credentials_source?: string;
};

function isOwnAccountCarrier(carrier?: string): boolean {
  return (OWN_ACCOUNT_CARRIERS as readonly string[]).includes(
    (carrier || '').toLowerCase(),
  );
}

export function normalizeAccountNumber(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number'
    ? String(value).replace(/[\s-]/g, '').toUpperCase()
    : '';
}

/** The only part of an account number that is ever shown. */
export function accountLast4(value: unknown): string {
  return normalizeAccountNumber(value).slice(-4);
}

/** The workspace's own accounts, from the settings carrier list. */
export function connectedCarrierAccounts(
  carriers?: CarrierSettingsEntry[] | null,
): ConnectedCarrierAccounts {
  const accounts: ConnectedCarrierAccounts = {};

  (carriers || []).forEach(entry => {
    const carrier = entry.carrier?.value?.toLowerCase();

    if (!entry.credentials || !carrier || !isOwnAccountCarrier(carrier)) {
      return;
    }

    const number = normalizeAccountNumber(
      entry.existing_fields?.account_number?.value ??
        entry.existing_fields?.shipper_number?.value,
    );

    if (number) {
      accounts[carrier] = number;
    }
  });

  return accounts;
}

/**
 * The last four digits of the workspace's account when this rate was quoted on
 * it; undefined for a rate quoted on anyone else's account.
 */
export function ownAccountLast4(
  carrier: string | undefined,
  rate: QuotedRate,
  accounts?: ConnectedCarrierAccounts,
): string | undefined {
  const key = (carrier || '').toLowerCase();
  const connected = accounts?.[key];

  if (
    !connected ||
    !isOwnAccountCarrier(key) ||
    rate.credentials_source !== 'organization' ||
    normalizeAccountNumber(rate.credentials_id) !== connected
  ) {
    return undefined;
  }

  return accountLast4(connected);
}

/** Up to two initials for a workspace without a logo: "Lights.com" → "LC". */
export function workspaceInitials(name?: string): string {
  const words = (name || '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) {
    return '?';
  }

  const initials =
    words.length === 1
      ? words[0].slice(0, 2)
      : `${words[0][0]}${words[words.length - 1][0]}`;

  return initials.toUpperCase();
}
